import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion, requestIdleCallback } from '@theme/utilities';

const DEFAULT_PRODUCT_INFORMATION_SECTION_ID = 'main';
const PRODUCT_GRID_SELECTOR = '[data-product-grid-content]';
const QUICK_ADD_CHOOSE_BUTTON_SELECTOR = '.quick-add__button--choose';
const QUICK_ADD_ADD_BUTTON_SELECTOR = '.quick-add__button--add';
const QUICK_ADD_CONTENT_CACHE_LIMIT = 30;
const INFINITE_DEFER_ATTRIBUTE = 'data-infinite-defer-init';

function scheduleAfterAppend(callback) {
  requestAnimationFrame(() => {
    requestIdleCallback(callback);
  });
}

/** @type {Map<string, Element>} */
const quickAddContentCache = new Map();
/** @type {Map<string, Promise<Element | null>>} */
const pendingQuickAddContent = new Map();

let activeQuickAddModalRequestId = 0;
let quickAddCacheGeneration = 0;

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {AbortController | null} */
  #cartUpdateAbortController = null;
  /** @type {AbortController | null} */
  #modalVariantAbortController = null;
  /** @type {boolean} */
  #shouldResetRequiredSizeSelection = false;
  /** @type {(event: VariantSelectedEvent) => void} */
  #boundVariantSelectedHandler = this.#updateQuickAddButtonState.bind(this);

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    const selectedOptionValueIds = this.#getSelectedOptionValueIds();
    if (selectedOptionValueIds.length) {
      url.searchParams.delete('variant');
      url.searchParams.set('option_values', selectedOptionValueIds.join(','));
    }

    return url.toString();
  }

  get productSectionId() {
    return this.dataset.productSectionId || DEFAULT_PRODUCT_INFORMATION_SECTION_ID;
  }

  /**
   * Gets the currently selected option value IDs from the product card.
   * Preserves card context like color without forcing a full variant/size selection.
   * @returns {string[]}
   */
  #getSelectedOptionValueIds() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (!productCard) return [];

    return Array.from(productCard.querySelectorAll('input[type="radio"]:checked[data-option-value-id]'))
      .filter((input) => !input.closest('fieldset[data-option-name="size"]'))
      .map((input) => /** @type {HTMLInputElement} */ (input).dataset.optionValueId)
      .filter((optionValueId) => typeof optionValueId === 'string');
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.closest(`[${INFINITE_DEFER_ATTRIBUTE}]`)) {
      scheduleAfterAppend(() => {
        if (this.isConnected) this.#connectGlobalListeners();
      });
      return;
    }

    this.#connectGlobalListeners();
  }

  #connectGlobalListeners() {
    this.#cartUpdateAbortController?.abort();
    this.#cartUpdateAbortController = new AbortController();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#boundVariantSelectedHandler);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController?.abort();
    this.#modalVariantAbortController?.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#boundVariantSelectedHandler);
  }

  /**
   * Clears cached content and syncs direct-add inventory state when cart is updated.
   * @param {CartUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    quickAddCacheGeneration += 1;
    quickAddContentCache.clear();
    pendingQuickAddContent.clear();

    this.#syncDirectAddInventoryState(event);
  };

  /**
   * Hides direct-add buttons when all available inventory for that variant is already in the cart.
   * This only applies to direct-add cards; choose-option cards must stay available for other variants.
   * @param {CartUpdateEvent} event
   */
  #syncDirectAddInventoryState(event) {
    if (event.detail?.data?.didError) return;
    if (this.dataset.quickAddButton !== 'add') return;

    const variantId = this.dataset.variantId;
    const maxQuantity = Number(this.dataset.maxQuantity);

    if (!variantId || !Number.isFinite(maxQuantity)) return;

    const items = /** @type {{ items?: Array<{ quantity?: number, variant_id?: number | string, id?: number | string }> }} */ (
      event.detail?.resource
    )?.items;
    let cartQuantity = Array.isArray(items)
      ? items.reduce((quantity, item) => {
          const itemVariantId = item.variant_id ?? item.id;
          return String(itemVariantId) === String(variantId) ? quantity + (Number(item.quantity) || 0) : quantity;
        }, 0)
      : null;

    if (cartQuantity === null && event.detail?.data?.source === 'product-form-component') {
      const eventVariantId = String(event.detail?.data?.variantId || '');
      const itemCountDelta = Number(event.detail?.data?.itemCountDelta || 0);
      if (eventVariantId === String(variantId) && Number.isFinite(itemCountDelta)) {
        cartQuantity = Number(this.dataset.cartQuantity || 0) + itemCountDelta;
      }
    }

    if (cartQuantity === null || !Number.isFinite(cartQuantity)) return;

    this.#setDirectAddExhaustedState(cartQuantity >= maxQuantity, cartQuantity);
  }

  /**
   * @param {boolean} exhausted
   * @param {number} cartQuantity
   */
  #setDirectAddExhaustedState(exhausted, cartQuantity) {
    this.dataset.cartQuantity = String(cartQuantity);
    this.dataset.quickAddExhausted = String(exhausted);

    this.querySelectorAll(QUICK_ADD_ADD_BUTTON_SELECTOR).forEach((button) => {
      if (button instanceof HTMLButtonElement) {
        button.disabled = exhausted;
      }
    });

    this.querySelectorAll('input[name="id"]').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.disabled = exhausted;
      }
    });
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    if (this.hasAttribute('loading')) return;

    const currentUrl = this.productPageUrl;
    if (!currentUrl) return;

    const requestId = ++activeQuickAddModalRequestId;
    const cacheKey = this.#getProductGridCacheKey(currentUrl);
    const cachedProductGrid = quickAddContentCache.get(cacheKey);

    if (cachedProductGrid) {
      this.#setCachedProductGrid(cacheKey, cachedProductGrid);
      this.#openWithProductGrid(cachedProductGrid);
      return;
    }

    this.#setCardLoadingState(true);

    let productGrid = null;

    try {
      productGrid = await this.#getProductGridContent(currentUrl, this.productSectionId);
    } catch (error) {
      if (error?.name !== 'AbortError') console.error(error);
    } finally {
      this.#setCardLoadingState(false);
    }

    if (requestId !== activeQuickAddModalRequestId) return;

    if (!productGrid) return;

    this.#openWithProductGrid(productGrid);
  };

  /**
   * Warms the modal content cache before the user clicks quick add.
   *
   * @param {string} [productPageUrl]
   * @returns {Promise<Element | null> | undefined}
   */
  preloadProductPage(productPageUrl = this.productPageUrl) {
    if (!productPageUrl) return undefined;

    return this.#getProductGridContent(productPageUrl, this.productSectionId).catch((error) => {
      if (error?.name !== 'AbortError') console.error(error);
      return null;
    });
  }

  handlePreload = () => {
    this.preloadProductPage();
  };

  /**
   * @param {boolean} isLoading
   */
  #setCardLoadingState(isLoading) {
    this.toggleAttribute('loading', isLoading);
    this.setAttribute('aria-busy', String(isLoading));

    this.querySelectorAll(QUICK_ADD_CHOOSE_BUTTON_SELECTOR).forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      button.toggleAttribute('aria-busy', isLoading);
      button.toggleAttribute('aria-disabled', isLoading);
      button.toggleAttribute('data-loading', isLoading);
    });
  }

  /**
   * Gets product-grid modal content from cache, an in-flight request, or the product page.
   *
   * @param {string} productPageUrl
   * @param {string} sectionId
   * @returns {Promise<Element | null>}
   */
  async #getProductGridContent(productPageUrl, sectionId = this.productSectionId) {
    const cacheKey = this.#getProductGridCacheKey(productPageUrl, sectionId);
    const cachedContent = quickAddContentCache.get(cacheKey);
    if (cachedContent) {
      this.#setCachedProductGrid(cacheKey, cachedContent);
      return cachedContent;
    }

    const pendingContent = pendingQuickAddContent.get(cacheKey);
    if (pendingContent) return pendingContent;

    const generation = quickAddCacheGeneration;
    const contentPromise = this.fetchProductPage(productPageUrl, sectionId)
      .then((html) => {
        const gridElement = html?.querySelector(PRODUCT_GRID_SELECTOR);
        if (!gridElement) return null;

        const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
        if (generation === quickAddCacheGeneration) {
          this.#setCachedProductGrid(cacheKey, productGrid);
        }

        return productGrid;
      })
      .finally(() => {
        pendingQuickAddContent.delete(cacheKey);
      });

    pendingQuickAddContent.set(cacheKey, contentPromise);

    return contentPromise;
  }

  /**
   * @param {string} cacheKey
   * @param {Element} productGrid
   */
  #setCachedProductGrid(cacheKey, productGrid) {
    if (quickAddContentCache.has(cacheKey)) {
      quickAddContentCache.delete(cacheKey);
    }

    quickAddContentCache.set(cacheKey, productGrid);

    while (quickAddContentCache.size > QUICK_ADD_CONTENT_CACHE_LIMIT) {
      const oldestKey = quickAddContentCache.keys().next().value;
      if (!oldestKey) break;

      quickAddContentCache.delete(oldestKey);
    }
  }

  /**
   * @param {string} productPageUrl
   * @param {string} [sectionId]
   * @returns {string}
   */
  #getProductGridCacheKey(productPageUrl, sectionId = this.productSectionId) {
    return `${sectionId}::${productPageUrl}`;
  }

  /**
   * @param {Element} productGrid
   */
  #openWithProductGrid(productGrid) {
    this.updateQuickAddModal(/** @type {Element} */ (productGrid.cloneNode(true)));
    this.#openQuickAddModal();
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @param {string} [sectionId] - The product information section ID to render
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl, sectionId = this.productSectionId) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      try {
        const sectionHtml = await this.#fetchProductPageDocument(
          this.#getProductInformationSectionUrl(productPageUrl, sectionId)
        );
        if (sectionHtml?.querySelector(PRODUCT_GRID_SELECTOR)) return sectionHtml;
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        console.warn(error);
      }

      return this.#fetchProductPageDocument(productPageUrl);
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * @param {string} url
   * @returns {Promise<Document>}
   */
  async #fetchProductPageDocument(url) {
    const response = await fetch(url, {
      signal: this.#abortController?.signal,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
    }

    const responseText = await response.text();
    return new DOMParser().parseFromString(responseText, 'text/html');
  }

  /**
   * Builds a lightweight Section Rendering API URL for quick add content.
   * @param {string} productPageUrl
   * @param {string} sectionId
   * @returns {string}
   */
  #getProductInformationSectionUrl(productPageUrl, sectionId) {
    const url = new URL(productPageUrl);
    url.searchParams.set('section_id', sectionId || DEFAULT_PRODUCT_INFORMATION_SECTION_ID);
    url.searchParams.sort();

    return url.toString();
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    // Quick add keeps non-size card context (for example color) but forces size to be chosen in the modal.
    this.#shouldResetRequiredSizeSelection = this.#requiresExplicitSizeSelection(productGrid);
    this.#resetRequiredSizeSelection(productGrid);

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');
      const productPageUrl = this.productPageUrl;

      const productBadge = productDetails?.querySelector('.d-product-badges');
      const productVendor = productDetails?.querySelector('.view-product-title.view-product-vendor');
      const productTitle = productDetails?.querySelector('.view-product-title:not(.view-product-vendor)');

      /**
       * @param {Element | null | undefined} metaBlock
       * @param {string} className
       * @param {string} text
       */
      const appendProductMeta = (metaBlock, className, text) => {
        if (metaBlock instanceof HTMLElement) {
          productHeader.appendChild(metaBlock);
          return;
        }

        if (!text) return;

        const wrapper = document.createElement('div');
        wrapper.className = className;

        if (className === 'd-product-badges') {
          wrapper.textContent = text;
          productHeader.appendChild(wrapper);
          return;
        }

        const link = document.createElement('a');
        link.className = 'link';
        link.textContent = text;
        link.href = productPageUrl;

        if (className.includes('view-product-vendor')) {
          link.classList.add('view-product-vendor');
        }

        wrapper.appendChild(link);
        productHeader.appendChild(wrapper);
      };

      appendProductMeta(productBadge, 'd-product-badges', this.dataset.productBadge || '');
      appendProductMeta(productVendor, 'view-product-title view-product-vendor', this.dataset.productVendor || '');
      appendProductMeta(productTitle, 'view-product-title', this.dataset.productTitle || '');

      if (productPrice) {
        productHeader.appendChild(productPrice);
        // 👇 Duties & Taxes block
        const dutiesTaxes = document.createElement('div');
        dutiesTaxes.className = 'duties-taxes';

        const dutiesText = document.createElement('div');
        dutiesText.className = 'duties-taxes__text';
        dutiesText.textContent = 'Incl. Duties & Taxes.';

        dutiesTaxes.appendChild(dutiesText);

        productHeader.appendChild(dutiesTaxes);
      }

      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#modalVariantAbortController?.abort();
    this.#modalVariantAbortController = new AbortController();
    const variantUpdateTarget = document.getElementById('quick-add-dialog') || modalContent;
    variantUpdateTarget.addEventListener(
      ThemeEvents.variantUpdate,
      () => {
        requestAnimationFrame(() => {
          this.#syncQuickAddModalState(modalContent);
        });
      },
      { signal: this.#modalVariantAbortController.signal }
    );

    this.#syncQuickAddModalState(modalContent);
    this.#syncVariantSelection(modalContent);
    this.#setupQuickAddMedia(modalContent);
    requestAnimationFrame(() => this.#setupQuickAddMedia(modalContent));
  }

  /**
   * Keeps the modal's required-size contract stable across initial render and variant rerenders.
   * @param {Element} modalContent
   */
  #syncQuickAddModalState(modalContent) {
    if (this.#shouldResetRequiredSizeSelection && !this.#hasSelectedSize(modalContent)) {
      this.#resetRequiredSizeSelection(modalContent);
    } else if (this.#hasSelectedSize(modalContent)) {
      this.#shouldResetRequiredSizeSelection = false;
    }

    this.#moveProductBadgeAboveTitles(modalContent);
  }

  /**
   * Clears any server-rendered size selection in quick add when size must be chosen explicitly.
   * This preserves layout while preventing default size selection from bypassing validation.
   * @param {Element} modalContent
   */
  #resetRequiredSizeSelection(modalContent) {
    const productForm = modalContent.querySelector('product-form-component[data-require-size-selection="true"]');
    if (!(productForm instanceof HTMLElement)) return;

    const sizeFieldset = modalContent.querySelector('fieldset[data-option-name="size"]');
    if (sizeFieldset instanceof HTMLFieldSetElement) {
      const radios = Array.from(sizeFieldset.querySelectorAll('input[type="radio"]')).filter(
        (radio) => radio instanceof HTMLInputElement
      );

      if (radios.length > 1) {
        for (const radio of radios) {
          radio.checked = false;
          radio.removeAttribute('checked');
          radio.dataset.currentChecked = 'false';
          radio.dataset.previousChecked = 'false';
        }
      }
    }

    const sizeSelect = modalContent.querySelector('select[data-option-name="size"]');
    if (sizeSelect instanceof HTMLSelectElement) {
      const selectableOptions = Array.from(sizeSelect.options).filter((option) => option.dataset.optionValueId);

      if (selectableOptions.length > 1) {
        let placeholder = Array.from(sizeSelect.options).find(
          (option) => option.value === '' && !option.dataset.optionValueId
        );

        if (!placeholder) {
          placeholder = document.createElement('option');
          placeholder.value = '';
          placeholder.disabled = true;
          placeholder.textContent = 'Select Size';
          sizeSelect.insertBefore(placeholder, sizeSelect.firstChild);
        }

        for (const option of sizeSelect.options) {
          option.removeAttribute('selected');
        }

        placeholder.selected = true;
        placeholder.setAttribute('selected', 'selected');
        sizeSelect.value = '';
      }
    }

    const variantIdInput = productForm.querySelector('input[name="id"]');
    if (variantIdInput instanceof HTMLInputElement) {
      variantIdInput.value = '';
    }
  }

  /**
   * @param {Element} modalContent
   * @returns {boolean}
   */
  #requiresExplicitSizeSelection(modalContent) {
    const productForm = modalContent.querySelector('product-form-component[data-require-size-selection="true"]');
    return productForm instanceof HTMLElement;
  }

  /**
   * @param {Element} modalContent
   * @returns {boolean}
   */
  #hasSelectedSize(modalContent) {
    const sizeFieldset = modalContent.querySelector('fieldset[data-option-name="size"]');
    if (sizeFieldset instanceof HTMLFieldSetElement && sizeFieldset.querySelector('input:checked')) {
      return true;
    }

    const sizeSelect = modalContent.querySelector('select[data-option-name="size"]');
    if (sizeSelect instanceof HTMLSelectElement) {
      const selectedOption = sizeSelect.options[sizeSelect.selectedIndex];
      return sizeSelect.value !== '' && !selectedOption?.disabled;
    }

    return false;
  }

  /**
   * Moves the quick-add badge block above the vendor/title rows in the modal DOM.
   * @param {Element} modalContent
   */
  #moveProductBadgeAboveTitles(modalContent) {
    const firstTitle = modalContent.querySelector('.product-details .view-product-title');
    if (!(firstTitle instanceof HTMLElement)) return;

    const badge = modalContent.querySelector('.product-details .d-product-badges');
    if (!(badge instanceof HTMLElement)) return;

    const badgeBlock = badge.closest('.product-badges-block');
    const badgeNode = badgeBlock instanceof HTMLElement ? badgeBlock : badge;
    const titleContainer = firstTitle.parentElement;

    if (!(titleContainer instanceof HTMLElement)) return;
    if (badgeNode === titleContainer.firstElementChild) return;

    titleContainer.insertBefore(badgeNode, firstTitle);
  }

  /**
   * Makes product media in quick add modal behave like a carousel and disables zoom interactions.
   * @param {Element} modalContent
   */
  #setupQuickAddMedia(modalContent) {
    const mediaGallery = modalContent.querySelector('.product-information__media media-gallery');
    if (!(mediaGallery instanceof HTMLElement)) return;

    mediaGallery.classList.add('media-gallery--carousel');
    mediaGallery.dataset.presentation = 'carousel';

    const slideshow = mediaGallery.querySelector('slideshow-component');
    if (!(slideshow instanceof HTMLElement)) return;

    // Keep quick-add interaction focused on variant/cart flow, not nested zoom.
    mediaGallery.querySelector('zoom-dialog')?.remove();
    mediaGallery.querySelectorAll('.product-media-container__zoom-button').forEach((button) => button.remove());
    mediaGallery.querySelectorAll('.product-media-container--zoomable').forEach((container) => {
      container.classList.remove('product-media-container--zoomable');
    });
    slideshow.querySelectorAll('slideshow-slide[on\\:click]').forEach((slide) => {
      slide.removeAttribute('on:click');
    });

    // If only mobile controls were rendered, make them visible in the modal on desktop too.
    mediaGallery.querySelectorAll('slideshow-controls.desktop\\:hidden').forEach((controls) => {
      controls.classList.remove('desktop:hidden');
    });
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedOptionValueIds = this.#getSelectedOptionValueIds();
    if (!selectedOptionValueIds.length) return;

    for (const optionValueId of selectedOptionValueIds) {
      const modalInput = modalContent.querySelector(`input[type="radio"][data-option-value-id="${optionValueId}"]`);

      if (modalInput instanceof HTMLInputElement && !modalInput.checked) {
        modalInput.checked = true;
        modalInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title:not(.view-product-vendor) a')
    );

    if (!anchorElement) return;

    this.querySelectorAll('.view-product-title a, .product-header a').forEach((link) => {
      if (link instanceof HTMLAnchorElement) {
        link.href = anchorElement.href;
      }
    });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
